class NewsletterPopup extends HTMLElement {
  constructor() {
    super();
    this.popup = this.querySelector("div");
    this.closeButton = this.querySelector("#close-newsletter");
    this.init();
  }

  init() {
    // Check local storage to see if the pop-up should be shown
    if (localStorage.getItem("newsletterDismissed")) {
      this.popup.removeAttribute("open");
      return;
    }

    // if ?customer_posted=true#contact_form in the url dismiss the pop up
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.get("customer_posted") === "true") {
      this.popup.removeAttribute("open");
      localStorage.setItem("newsletterDismissed", "true");
      return;
    }

    // Automatically open the popup when the page loads
    setTimeout(() => {
      this.popup.setAttribute("open", "true");
    }, 500); // Delay slightly to ensure smooth transition

    // Close button logic
    this.closeButton.addEventListener("click", () => {
      this.popup.removeAttribute("open");
      // local storage after 5 seconds
      setTimeout(() => {
        localStorage.setItem("newsletterDismissed", "true");
      }, 500);
    });
  }
}

// Define the custom element
customElements.define("newsletter-popup", NewsletterPopup);

/**
 * CartItem
 *
 * Quantity changes target the cart LINE NUMBER, not the variant id. A cart can
 * legitimately contain several lines of the same variant (Buy X Get Y discounts
 * split the discounted units onto their own line), and addressing by variant id
 * is ambiguous in that case — it updates whichever line matches first and the
 * totals go wrong.
 */
class CartItem extends HTMLElement {
  constructor() {
    super();

    this.decreaseButton = this.querySelector("[data-qty-decrease]");
    this.increaseButton = this.querySelector("[data-qty-increase]");
    this.quantityElement = this.querySelector("[data-qty-value]");

    if (!this.decreaseButton || !this.increaseButton || !this.quantityElement) return;

    this.quantity = parseInt(this.quantityElement.textContent.trim(), 10);
    this.line = parseInt(this.getAttribute("data-line"), 10);
    this.busy = false;

    this.handleQuantity = this.handleQuantity.bind(this);

    this.decreaseButton.addEventListener("click", this.handleQuantity);
    this.increaseButton.addEventListener("click", this.handleQuantity);
  }

  async handleQuantity(e) {
    e.preventDefault();

    // Guard against double-fire / rapid clicking while a request is in flight.
    if (this.busy) return;

    const button = e.target.closest("[data-qty-decrease], [data-qty-increase]");
    if (!button) return;

    const isDecrease = button.hasAttribute("data-qty-decrease");
    const cartDrawer = document.querySelector("cart-drawer");
    if (!cartDrawer || !Number.isFinite(this.line)) return;

    const nextQuantity = isDecrease ? this.quantity - 1 : this.quantity + 1;
    if (nextQuantity < 0) return;

    this.busy = true;
    this.decreaseButton.disabled = true;
    this.increaseButton.disabled = true;

    try {
      await cartDrawer.updateQuantity(this.line, nextQuantity);
    } finally {
      // The drawer re-renders after updating, replacing this element, so
      // re-enabling is only relevant if that didn't happen.
      this.busy = false;
      if (this.isConnected) {
        this.decreaseButton.disabled = false;
        this.increaseButton.disabled = false;
      }
    }
  }
}

if (!customElements.get("cart-item")) {
  customElements.define("cart-item", CartItem);
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.openDrawer = this.openDrawer.bind(this);
    this.closeDrawer = this.closeDrawer.bind(this);

    this.addEventListener("click", this.onClick);
  }

  onClick(e) {
    // Close the cart if the user clicks outside of the cart
    const isCloseButton = e.target.closest("#close");

    if (e.target.id === "cart" || isCloseButton) {
      e.preventDefault();

      if (window.location.pathname === "/cart") {
        window.location.href = "/";
      } else {
        this.closeDrawer();
      }
    }
  }

  openDrawer() {
    this.setAttribute("open", "true");
  }

  closeDrawer() {
    this.removeAttribute("open");
  }

  /**
   * @param {number} line 1-based cart line number
   * @param {number} quantity new quantity (0 removes the line)
   */
  async updateQuantity(line, quantity) {
    try {
      await fetch("/cart/change.js", {
        method: "post",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          line: line,
          quantity: quantity,
        }),
      });
    } catch (err) {
      console.error("Cart update failed", err);
    }

    // Update the cart drawer after the quantity change
    await this.update();
  }

  async update() {
    const res = await fetch("/?section_id=cart-drawer");
    const text = await res.text();
    const html = document.createElement("div");
    html.innerHTML = text;

    const cartDrawer = document.getElementById("cart");
    if (cartDrawer) {
      cartDrawer.innerHTML = html.querySelector("#cart").innerHTML;
    }

    if (window.initUpsellSwiper) {
      window.initUpsellSwiper();
    }

    const countHelper = html.querySelector("#item-count-helper");
    const cartItemCount = countHelper ? countHelper.textContent : "0";

    // if cartItemCount is 0, hide the cart indicator
    const cartIndicators = document.querySelectorAll(".cart-indicator");
    if (cartIndicators) {
      cartIndicators.forEach((cartIndicator) => {
        cartIndicator.classList.toggle("hidden", cartItemCount === "0");
      });
    }
  }

  async addToCart(body) {
    await fetch("/cart/add", {
      method: "post",
      headers: { "Content-Type": "application/json" },
      body: body,
    })
      .then((res) => {
        console.log(res);
      })
      .catch((err) => {
        console.error(err);
      });
    // update cart
    await this.update().then(() => {
      this.openDrawer();
    });
  }

  async addObjectToCart(object) {
    await fetch("/cart/add.js", {
      method: "post",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(object),
    });

    // update cart
    await this.update().then(() => {
      // wait a tick then open drawer
      setTimeout(() => {
        this.openDrawer();
      }, 0);
    });
  }
}

if (!customElements.get("cart-drawer")) {
  customElements.define("cart-drawer", CartDrawer);
}

class HeaderCart extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.addEventListener("click", this.onClick);
  }

  onClick(e) {
    e.preventDefault();
    document.querySelector("cart-drawer").openDrawer();
  }
}

if (!customElements.get("header-cart")) {
  customElements.define("header-cart", HeaderCart);
}

// Turn into a more abstracted component for the drawer
class RemoveButton extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.addEventListener("click", this.onClick);
  }

  async onClick(e) {
    e.preventDefault();

    const form = this.closest("form");

    await fetch(e.target.href, {
      method: "post",
      body: new FormData(form),
    });

    // Fetch the updated cart data to check if it's empty
    const response = await fetch("/cart.js");
    const cartData = await response.json();

    // If the cart is empty, close the drawer
    if (cartData.item_count === 0) {
      document.querySelector("cart-drawer").closeDrawer();
    }

    // Update the cart drawer after removing the item
    await document.querySelector("cart-drawer").update();
  }
}

if (!customElements.get("remove-button")) {
  customElements.define("remove-button", RemoveButton);
}

class Newsletter extends HTMLElement {
  constructor() {
    super();
    // elements are input with id email, button with id subscribe
    this.email = this.querySelector("input[id='email']");
    this.button = this.querySelector("button[id='subscribe']");
    this.onClick = this.onClick.bind(this);

    // if ?customer_posted=true#contact_form is in the url, show a success message
    const urlParams = new URLSearchParams(window.location.search);
    const customerPosted = urlParams.get("customer_posted");
    if (customerPosted === "true") {
      this.changeStatus("Thank you for subscribing");
    }
    if (this.button) this.button.addEventListener("click", this.onClick);
  }
  async onClick(e) {
    if (this.email.value.length < 5) {
      this.changeStatus("Please enter a valid email ");
    }
  }

  async changeStatus(text) {
    this.querySelector("p").textContent = text;
  }
}

if (!customElements.get("newsletter")) {
  customElements.define("newsletter-widget", Newsletter);
}

class SlideShow extends HTMLElement {
  connectedCallback() {
    this.initSwiper();
  }

  initSwiper() {
    const swiperEl = this.querySelector(".mobile-swiper");
    if (!swiperEl) return;

    // Read the data-align attribute (default to 'left')
    const align = this.dataset.align || "left";
    const gap = this.dataset.gap || 0;

    // Map alignment to Swiper option
    const centeredSlides = align === "center";

    new Swiper(swiperEl, {
      slidesPerView: "auto",
      spaceBetween: parseInt(gap),
      speed: 400,
      centeredSlides: centeredSlides,
      mousewheel: {
        forceToAxis: true,
      },
    });
  }
}

if (!customElements.get("mobile-slideshow")) {
  customElements.define("mobile-slideshow", SlideShow);
}

class Dropdown extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("click", this.toggleAccordion.bind(this));

    // If the attribute openFirst is present, open the first accordion item
    if (this.hasAttribute("openFirst")) {
      const firstItem = this.querySelector("accordion-item");
      if (firstItem) {
        this.openAccordionItem(firstItem);
      }
    }
  }

  openAccordionItem(item) {
    item.setAttribute("open", "true");
    const reveal = item.querySelector("#accordion-reveal");
    if (reveal) {
      reveal.style.maxHeight = reveal.scrollHeight + "px";
      // wait 500ms, and then remove maxHeight to allow for dynamic content
      setTimeout(() => {
        reveal.style.maxHeight = "unset";
      }, 300);
    }

    // if pass data-section
    const blockId = item.getAttribute("data-block-id");
    if (blockId) {
      let targetImage = document.getElementById("image-" + blockId);
      if (targetImage) {
        //make all siblings aira-hidden true
        const siblings = targetImage.parentElement.children;
        Array.from(siblings).forEach((sibling) => {
          sibling.setAttribute("aria-hidden", "true");
        });

        //make target image aria-hidden false
        targetImage.removeAttribute("aria-hidden");
      }
    }
  }

  toggleAccordion(event) {
    const item = event.target.closest("accordion-item");
    if (!item) return;

    const isOpen = item.hasAttribute("open");

    // If openFirst is present and this is the only open item, prevent closing it
    if (this.hasAttribute("openFirst") && isOpen) {
      const openItems = this.querySelectorAll("accordion-item[open]");
      if (openItems.length === 1) {
        // Don’t allow closing the last open item
        return;
      }
    }

    // Close all accordion items
    this.closeAll();

    // Toggle the clicked item (open it if it was closed)
    if (!isOpen) {
      this.openAccordionItem(item);
    } else if (this.hasAttribute("openFirst")) {
      // If all are closed and openFirst is true, reopen the first one
      const firstItem = this.querySelector("accordion-item");
      if (firstItem) {
        this.openAccordionItem(firstItem);
      }
    }
  }

  closeAll() {
    this.querySelectorAll("accordion-item").forEach((item) => {
      const reveal = item.querySelector("#accordion-reveal");
      if (reveal && item.hasAttribute("open")) {
        reveal.style.maxHeight = reveal.scrollHeight + "px";
        // wait a tick then set maxHeight to 0 for transition
        setTimeout(() => {
          reveal.style.maxHeight = "0px";
        }, 0);
      }

      item.removeAttribute("open");
    });
  }
}

customElements.define("dropdown-element", Dropdown);
